<template>
  <div v-if="isAtLeastOneOutstandingSafe" class="outstanding-invoice">
    <h1>{{ tn("h1Heading") }}</h1>
    <ul>
      <li v-for="(sub, index) in subscriptionsWithDetails" :key="`sub${index}`" class="flex-start-md">
        <div>
          <svg-icon-component icon="circle-exclamation" class="icon-sm danger mr-half" />
        </div>
        <div class="truncate-flex-child-wrapper">
          <div>
            <i18n-t keypath="OutstandingInvoiceAlertComponent.line1" tag="strong" scope="global">
              <template #outstandingDays>
                {{ daysToDeleteBackup(sub) }}
              </template>
            </i18n-t>
            <i18n-t keypath="OutstandingInvoiceAlertComponent.line2" tag="div" scope="global">
              <template #invoiceNumber>
                <strong>{{ sub?.outstandingInvoice?.invoiceNumber }}</strong>
              </template>
            </i18n-t>
          </div>
          <i18n-t keypath="OutstandingInvoiceAlertComponent.line3" tag="div" scope="global" class="truncate-flex">
            <template #productName>
              {{ sub.productDisplayName }}
            </template>
            <template #computerName>
              <strong>{{ sub?.computer?.description }}</strong>
            </template>
          </i18n-t>
        </div>
        <div class="action">
          <button class="btn-outline-primary" @click="renewSafeSubscription(sub)">{{ tn("btnRenewNow") }}</button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, PropType } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { carboniteProducts } from "@/common/productsBySku";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import { INVOICE_FIRST_OUTSTANDING_DAY, INVOICE_LAST_OUTSTANDING_DAY, SUBSCRIPTION_MIN_WARNING_DAY } from "@/define";

const name = "OutstandingInvoiceAlertComponent";

export default defineComponent({
  name,
  components: {
    SvgIconComponent,
  },
  props: {
    subscriptions: {
      type: Array as PropType<ISubscription[]>,
      required: true,
    },
  },
  emits: ["renew-safe"],

  setup(props, context) {
    const daysAfterBackupDeleted = ref<number>(SUBSCRIPTION_MIN_WARNING_DAY);
    const isAtLeastOneOutstandingSafe = ref(false);

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${name}.${v}`, params);
    }

    // life cycle hooks
    onMounted(async () => {
      logEvent("created", name);
      checkIfAtLeastOneOutstandingSafe();
    });

    const subscriptionsWithDetails = computed(() => {
      return props.subscriptions.filter(s => showSubscriptionDetails(s));
    });

    function showSubscriptionDetails(sub: ISubscription): boolean {
      let showDetails = !!carboniteProducts.find(product => product.sku === sub.sku);
      if (sub.hasOutstandingInvoices && sub.outstandingInvoice) {
        showDetails &&=
          sub.outstandingInvoice.outstandingInvoiceDays <= INVOICE_FIRST_OUTSTANDING_DAY &&
          sub.outstandingInvoice.outstandingInvoiceDays >= INVOICE_LAST_OUTSTANDING_DAY;
      }
      return showDetails;
    }

    function daysToDeleteBackup(sub: ISubscription) {
      let days = daysAfterBackupDeleted.value;
      if (sub.outstandingInvoice) {
        days += sub.outstandingInvoice.outstandingInvoiceDays;
      }
      return Math.ceil(days);
    }

    function renewSafeSubscription(sub: ISubscription) {
      context.emit("renew-safe", sub);
    }

    function checkIfAtLeastOneOutstandingSafe() {
      for (const sub of props.subscriptions) {
        const isSafeSKU = carboniteProducts.find(product => product.sku === sub.sku && sub.hasOutstandingInvoices);
        if (isSafeSKU) {
          isAtLeastOneOutstandingSafe.value = true;
          return;
        }
      }
    }

    //expose to template
    return {
      tn,
      checkIfAtLeastOneOutstandingSafe,
      renewSafeSubscription,
      daysToDeleteBackup,
      showSubscriptionDetails,
      subscriptionsWithDetails,
      daysAfterBackupDeleted,
      isAtLeastOneOutstandingSafe,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";

.computer-name {
  max-width: 200px;
  display: flex;
}

.computer-name span {
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
